import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, Upload, ArrowLeft, Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoastPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("ANALYZING FAILURES...");
  const [result, setResult] = useState(null);

  // Loading text animation
  const loadingMessages = [
    "ANALYZING FAILURES...",
    "COUNTING RED FLAGS...",
    "LOCATING CRINGE...",
    "PREPARING DISAPPOINTMENT...",
    "CONSULTING HR DEMONS...",
    "MEASURING MEDIOCRITY..."
  ];

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    validateAndSetFile(droppedFile);
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    validateAndSetFile(selectedFile);
  };

  const validateAndSetFile = (file) => {
    if (!file) return;
    
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];
    
    const fileName = file.name.toLowerCase();
    const isValidType = validTypes.includes(file.type) || 
                        fileName.endsWith('.pdf') || 
                        fileName.endsWith('.docx') || 
                        fileName.endsWith('.doc');
    
    if (!isValidType) {
      toast.error("Please upload a PDF or Word document");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be under 10MB");
      return;
    }
    
    setFile(file);
    toast.success(`File loaded: ${file.name}`);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Please upload a resume first");
      return;
    }

    setIsLoading(true);
    let messageIndex = 0;
    const interval = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      setLoadingText(loadingMessages[messageIndex]);
    }, 1500);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/roast`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setResult(response.data);
      toast.success("Roast complete!");
    } catch (error) {
      console.error('Roast error:', error);
      toast.error(error.response?.data?.detail || "Failed to roast resume. Please try again.");
    } finally {
      clearInterval(interval);
      setIsLoading(false);
    }
  };

  const handleShare = () => {
    if (navigator.share && result) {
      navigator.share({
        title: 'My Torched.cv Score',
        text: `I scored ${result.score}/100 on Torched.cv: "${result.one_liner}"`,
        url: window.location.origin
      }).catch(() => {
        // User cancelled or share failed
        copyToClipboard();
      });
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    if (result) {
      const text = `My Torched.cv Score: ${result.score}/100 - "${result.one_liner}" - ${window.location.origin}`;
      navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    }
  };

  const resetForm = () => {
    setFile(null);
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Background effects */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-5"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1678185058395-ea4778917f13?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxmaXJlJTIwZGFya3xlbnwwfHx8fDE3NzYyMDE4NzN8MA&ixlib=rb-4.1.0&q=85')`
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/95 to-[#050505]" />
      <div className="absolute inset-0 grid-overlay pointer-events-none" />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Nav */}
        <nav className="w-full py-6 px-6 md:px-12 border-b border-white/10 bg-black/40 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-[#888888] hover:text-white transition-colors"
              data-testid="back-home-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm tracking-wide">Back</span>
            </button>
            <div className="flex items-center gap-2">
              <Flame className="w-6 h-6 text-[#FF3B30]" />
              <span className="font-clash text-xl font-bold tracking-tight">
                TORCHED<span className="text-[#FF3B30]">.CV</span>
              </span>
            </div>
            <div className="w-20" /> {/* Spacer */}
          </div>
        </nav>

        {/* Main content */}
        <main className="flex-1 flex items-center justify-center px-6 md:px-12 py-12">
          <div className="w-full max-w-3xl">
            {!result ? (
              // Upload Section
              <div className="animate-fade-in">
                <div className="text-center mb-12">
                  <h1 className="font-clash text-4xl sm:text-5xl font-bold tracking-tight uppercase mb-4">
                    <span className="text-[#FF3B30]">Roast</span> Mode
                  </h1>
                  <p className="text-[#888888] text-sm tracking-widest uppercase">
                    Prepare for disappointment
                  </p>
                </div>

                {/* Upload Zone */}
                <div
                  data-testid="upload-zone"
                  className={`upload-area mb-8 ${isDragging ? 'dragging' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileSelect}
                    data-testid="file-input"
                  />
                  
                  {file ? (
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-[#FF3B30]/20 flex items-center justify-center mb-4">
                        <Flame className="w-8 h-8 text-[#FF3B30]" />
                      </div>
                      <p className="text-[#F5F5F5] font-medium mb-2" data-testid="file-name">{file.name}</p>
                      <p className="text-[#888888] text-sm">Click to change file</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 border-2 border-dashed border-white/20 flex items-center justify-center mb-4">
                        <Upload className="w-8 h-8 text-[#888888]" />
                      </div>
                      <p className="text-[#F5F5F5] font-medium mb-2">Drop your resume here</p>
                      <p className="text-[#888888] text-sm">or click to browse (PDF, DOC, DOCX)</p>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  data-testid="submit-roast-btn"
                  onClick={handleSubmit}
                  disabled={!file || isLoading}
                  className={`w-full bg-[#FF3B30] text-white font-clash font-bold text-lg uppercase tracking-widest py-5 transition-all duration-150 ${
                    !file || isLoading 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-[#FF5544] hover:-translate-y-1'
                  }`}
                  style={{ boxShadow: file && !isLoading ? '4px 4px 0px white' : 'none' }}
                >
                  {isLoading ? (
                    <span className="font-mono animate-pulse">{loadingText}</span>
                  ) : (
                    <span className="flex items-center justify-center gap-3">
                      <Flame className="w-5 h-5" />
                      INCINERATE IT
                    </span>
                  )}
                </button>
              </div>
            ) : (
              // Results Section
              <div className="animate-slide-up">
                {/* Score Card */}
                <div 
                  className="shareable-card p-8 md:p-12 mb-8"
                  data-testid="roast-result-card"
                >
                  <div className="text-center mb-8">
                    <p className="text-[#888888] text-xs tracking-[0.3em] uppercase mb-4">
                      Your Resume Score
                    </p>
                    <div className="score-display text-7xl sm:text-8xl text-[#FF3B30] mb-2" data-testid="roast-result-score">
                      {result.score}<span className="text-3xl">/100</span>
                    </div>
                    <p className="font-clash text-xl uppercase tracking-wide text-[#F5F5F5]" data-testid="roast-score-label">
                      {result.score_label}
                    </p>
                  </div>

                  {/* One-liner */}
                  <div className="border-t border-white/10 pt-6">
                    <p className="font-mono text-center text-[#888888] text-sm italic" data-testid="roast-one-liner">
                      "{result.one_liner}"
                    </p>
                  </div>

                  {/* Share buttons */}
                  <div className="flex items-center justify-center gap-4 mt-6">
                    <button
                      onClick={handleShare}
                      className="flex items-center gap-2 px-6 py-3 bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors"
                      data-testid="share-btn"
                    >
                      <Share2 className="w-4 h-4" />
                      Share
                    </button>
                  </div>
                </div>

                {/* Full Roast */}
                <div className="bg-[#111111] border border-white/10 p-6 md:p-8 mb-8">
                  <h2 className="font-clash text-lg uppercase tracking-wide text-[#FF3B30] mb-6 flex items-center gap-3">
                    <Flame className="w-5 h-5" />
                    The Full Roast
                  </h2>
                  <div 
                    className="roast-text text-[#F5F5F5] whitespace-pre-wrap"
                    data-testid="roast-text"
                  >
                    {result.roast_text}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={resetForm}
                    className="flex-1 py-4 border border-white/20 text-white font-medium hover:bg-white/5 transition-colors"
                    data-testid="roast-again-btn"
                  >
                    Roast Another Resume
                  </button>
                  <button
                    onClick={() => navigate('/help')}
                    className="flex-1 py-4 bg-[#1A1A1A] text-white font-medium hover:bg-[#222] transition-colors"
                    data-testid="get-help-btn"
                  >
                    Get Actual Help Instead
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default RoastPage;
